import EmailIcon from "@mui/icons-material/Email";
import { Alert, Box, Paper, Typography } from "@mui/material";
import {
  Button,
  Field,
  FieldsContainer,
  globalMessageHandler,
  TextField,
} from "common";
import authService from "features/auth/authApi";
import I18n from "i18n";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(I18n.t("Auth.validation.emailRequired"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { success, data, error: apiError } = await authService.forgotPassword({
        email: email.trim(),
      });

      if (success) {
        setSuccess(true);
        globalMessageHandler.snack({
          message: data?.message || I18n.t("Auth.forgotPassword.sendResetLink"),
          severity: "success",
        });
      } else {
        const errorMessage = apiError?.error?.message || I18n.t("Auth.validation.networkError");
        setError(errorMessage);
        globalMessageHandler.snack({
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (err) {
      const errorMessage = I18n.t("Auth.validation.networkError");
      setError(errorMessage);
      globalMessageHandler.snack({
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          margin: "0 auto",
          mt: 4,
          textAlign: "center",
        }}
      >
        <EmailIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {I18n.t("Auth.forgotPassword.checkEmailTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {I18n.t("Auth.forgotPassword.checkEmailMessage")} <strong>{email}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {I18n.t("Auth.forgotPassword.checkEmailDescription")}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => navigate("/login")}
            variant="outlined"
            fullWidth
          >
            {I18n.t("Auth.forgotPassword.backToLogin")}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        margin: "0 auto",
        mt: 4,
      }}
    >
      <Typography variant="h4" gutterBottom textAlign="center">
        {I18n.t("Auth.forgotPassword.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph textAlign="center">
        {I18n.t("Auth.forgotPassword.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <FieldsContainer>
          <Field fullWidth>
            <TextField
              label={I18n.t("Auth.forgotPassword.emailLabel")}
              type="email"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              disabled={loading}
              error={error ? error : undefined}
            />
          </Field>
        </FieldsContainer>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={!email || loading}
          >
            {I18n.t("Auth.forgotPassword.sendResetLink")}
          </Button>
        </Box>

        <Box textAlign="center">
          <Typography variant="body2">
            {I18n.t("Auth.forgotPassword.rememberPassword")}{" "}
            <Link
              to="/login"
              style={{
                color: "inherit",
                textDecoration: "underline",
              }}
            >
              {I18n.t("Auth.forgotPassword.backToLogin")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}